/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import type { RendererProps } from './types'

import type { NormalizedChange } from '../diff/normalize'
import { ChangeList, Section } from '../primitives'

/**
 * Asset.
 *
 * An asset carries a wide, flat set of fields covering three unrelated concerns, so a single list
 * of changes reads as noise. Grouping them tells the reader immediately whether this edit was an
 * administrative rename, a reassignment, or a configuration change.
 */

const SECTIONS: { title: string; fields: string[] }[] = [
  {
    title: 'Identity',
    fields: ['name', 'assetName', 'registration', 'vin', 'make', 'model', 'assetType', 'description'],
  },
  {
    title: 'Assignment',
    fields: ['companyId', 'company', 'siteId', 'site', 'depotId', 'depot', 'umdId', 'tagId', 'driverId'],
  },
  {
    title: 'Configuration',
    fields: [
      'fridgeType',
      'temperatureZone',
      'setPoint',
      'minTemperature',
      'maxTemperature',
      'enabled',
      'isActive',
      'deleted',
    ],
  },
]

export const AssetRenderer: React.FC<RendererProps> = ({ changes }) => {
  const claimed = new Set<NormalizedChange>()

  const sections = SECTIONS.map(({ title, fields }) => {
    const matched = changes.filter((c) => !claimed.has(c) && fields.includes(c.segments[0]))
    matched.forEach((c) => claimed.add(c))
    return { title, changes: matched }
  }).filter((section) => section.changes.length > 0)

  // Fields outside the three groups still have to appear — an asset schema change must never
  // cause a change to vanish from the panel.
  const remaining = changes.filter((c) => !claimed.has(c))

  return (
    <div className="flex flex-col gap-3">
      {sections.map((section) => (
        <Section key={section.title} title={section.title} count={section.changes.length}>
          <ChangeList changes={section.changes} />
        </Section>
      ))}

      {remaining.length > 0 && (
        <Section title="Other changes" count={remaining.length}>
          <ChangeList changes={remaining} />
        </Section>
      )}
    </div>
  )
}
